if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();

      this._onKeyup = this.onKeyup.bind(this);
      this._onToggleClick = this.onToggleClick.bind(this);
      this._onScrollSticky = this.setStickyClass.bind(this);
      this._onScrollOffset = this.setHeaderOffset.bind(this);
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      if (!this.menu || !this.toggle) return;

      // El drawer nuevo (.mobile-menu-drawer) trae su propio script inline que es el
      // dueno UNICO del toggle, Escape y el atributo [open] del <details>. Cuando existe,
      // ThemeHeader NO debe bindear el click ni Escape del mobile: el doble binding
      // desincronizaba el icono X (closeAnimation borraba [open] a los 400ms mientras el
      // inline lo mantenia abierto). Ver snippets/header-mobile-menu.liquid.
      const ownsMobileToggle = !this.menu.classList.contains('mobile-menu-drawer');

      if (ownsMobileToggle) {
        document.addEventListener('keyup', this._onKeyup);
      }

      if (this.classList.contains('header-sticky--active')) {
        document.body.classList.add('header-sticky--active');
      }

      this.toggleButton = this.toggle.querySelector('.mobile-toggle');
      if (ownsMobileToggle) {
        this.toggleButton.addEventListener('click', this._onToggleClick);
      }

      window.addEventListener('scroll', this._onScrollSticky, {
        passive: true
      });

      window.addEventListener('scroll', this._onScrollOffset, {
        passive: true
      });
      this.setHeaderHeight();
      this._sizeObserver = new ResizeObserver(() => {
        this.setHeaderHeight();
        this.setAnnouncementHeight();
      });
      this._sizeObserver.observe(this, { box: 'border-box' });

      window.dispatchEvent(new Event('scroll'));

      this.announcementBar = document.querySelector('.announcement-bar-section');
      if (this.announcementBar) {
        this._sizeObserver.observe(this.announcementBar);
        this.setAnnouncementHeight();
        window.dispatchEvent(new Event('resize'));
      }

      this._mobileNavTimeout = setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    disconnectedCallback() {
      document.removeEventListener('keyup', this._onKeyup);
      if (this.toggleButton) this.toggleButton.removeEventListener('click', this._onToggleClick);
      window.removeEventListener('scroll', this._onScrollSticky);
      window.removeEventListener('scroll', this._onScrollOffset);
      clearTimeout(this._mobileNavTimeout);
      this._sizeObserver?.disconnect();
    }
    onKeyup(e) {
      if (e.code && e.code.toUpperCase() === 'ESCAPE') {
        const wasOpen = this.toggle.classList.contains('active');
        this.toggle.removeAttribute('open');
        this.toggle.classList.remove('active');
        if (wasOpen && this.toggleButton) {
          this.toggleButton.focus();
        }
      }
    }
    onToggleClick(e) {
      if (this.toggle.classList.contains('active')) {
        e.preventDefault();
        this.toggle.classList.remove('active');
        this.closeAnimation(this.toggle);
      } else {
        setTimeout(() => {
          this.toggle.classList.add('active');
        });
      }
    }
    // El header no se esconde al scrollear: queda siempre visible. Esconderlo requeria
    // un transform, y un ancestro con transform es el bloque contenedor de sus
    // descendientes position:fixed -el cart drawer y el menu mobile viven adentro del
    // header-. Ver el comentario en sections/header.liquid antes de reintroducirlo.
    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const a_bar = document.querySelector('.announcement-bar-section');
      if (!a_bar) {
        document.documentElement.style.setProperty('--announcement-height', '0px');
        return;
      }
      let h = a_bar.clientHeight;
      document.documentElement.style.setProperty('--announcement-height', h + 'px');
    }
    setHeaderOffset() {
      if (!this.header_section) return;
      let h = this.header_section.getBoundingClientRect().top;
      document.documentElement.style.setProperty('--header-offset', h + 'px');
    }
    setHeaderHeight() {
      let h = this.clientHeight;
      document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
      this.submenus = this.querySelectorAll('.thb-full-menu > .menu-item-has-children:not(.menu-item-has-megamenu) > .sub-menu');
    }

    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;

      document.fonts.ready.then(() => {
        window.addEventListener('resize', debounce(() => {
          _this.resizeSubMenus();
        }, 100));
      });
    }

    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        const subSubmenus = submenu.querySelectorAll(':scope > .menu-item-has-children > .sub-menu');

        subSubmenus.forEach((subSubmenu) => {
          const submenuRect = subSubmenu.getBoundingClientRect();
          const submenuRightEdge = submenuRect.left + submenuRect.width;
          const submenuIsOverflowing = submenuRightEdge > window.innerWidth;

          if (submenuIsOverflowing) {
            subSubmenu.parentElement.classList.add('left-submenu');
          } else {
            subSubmenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }

  customElements.define('full-menu', FullMenu);
}
